<template>
  <div>
    <BeaconHeader :current-time="currentTime" />


    <b-container fluid class="mt--6">
                <StatusCard 
                  :socket-connected="socketConnected"
                  @refresh="loadEventHistory"
                  @assign="openAssignmentModal"
                  @config="openConfigModal"
                />

      <!-- Statistics Cards -->
       <BeaconStats :stats="stats" />

      <b-row class="mt-4">
        <!-- Live Activity Feed - ENHANCED WITH FILTERS -->
          <b-col xl="4">
            <LiveActivityFeed
              :recent-events="recentEvents"
              :active-devices-list="activeDevicesList"
              :live-filters="liveFilters"
              @update-filters="applyLiveFilters"
              @clear-filter="clearLiveFilter"
              @clear-all-filters="clearAllLiveFilters"
            />
          </b-col>

        <!-- Active Beacons -->
        <b-col xl="4">
          <ActiveBeacons :active-beacons="activeBeacons" />
        </b-col>
        
        <!-- Active Devices -->
           <b-col xl="4">
          <ActiveDevices 
            :active-devices-list="activeDevicesList"
            :format-time-ago="formatTimeAgo"
          />
        </b-col>
      </b-row>

      <!-- Event History Table WITH FILTERS -->
      <EventHistory
        :all-events="allEvents"
        :filtered-events="filteredHistoryEvents"
        :history-filters.sync="historyFilters"
        :loading="loading"
        :unique-devices="uniqueDevices"
        :unique-patients="uniquePatients"
        :format-time="formatTime"
        :get-nurse-name="getNurseName"
        :get-event-type-label="getEventTypeLabel"
        :get-event-type-badge-variant="getEventTypeBadgeVariant"
        :get-rssi-badge-variant="getRssiBadgeVariant"
        @update:filters="handleFiltersUpdate"
        @filter-change="applyHistoryFilters"
        @export="exportEventHistory"
      />
    </b-container>

    <!-- Device Assignment Modal -->

    <!-- Replaced Device Assignment Modal with Component -->
    <!-- In BeaconDemo.vue, update the DeviceAssignmentModal props -->
      <DeviceAssignmentModal
      v-if="assignmentModalVisible"
      :visible.sync="assignmentModalVisible"
      :nurse-assignment-modal-visible.sync="nurseAssignmentModalVisible"
      :provisioning-modal-visible.sync="provisioningModalVisible"
      :assignment-form.sync="assignmentForm"
      :available-devices="availableDevices"
      :assignments="assignments"
      :sites="sites"
      :groups="groups"
      :selected-devices.sync="selectedDevices" 
      :loading-assignments="loadingAssignments"
      :is-assigning="isAssigning"
      :provisioning-progress="provisioningProgress"
      :provisioning-message="provisioningMessage"
      :is-provisioning="isProvisioning"
      :provisioning-devices="provisioningDevices"
      :search-query.sync="deviceSearchQuery"
      :selected-site.sync="selectedSite"
      :selected-group.sync="selectedGroup"
      @filter-site="filterDevicesBySite"
      @filter-group="filterDevicesByGroup"
      @open-assign-modal="openNurseAssignmentModal"
      @unassign="unassignDevice"
      @delete-assignment="deleteAssignment"
      @assign="handleComponentAssign" 
      @cancel-assign="cancelAssignForm"
      @close-provisioning="handleCloseProvisioningModal"
    />

    <!-- Configuration Modal -->
      <!-- Provisioning Progress Modal -->
      <b-modal
        v-model="provisioningModalVisible"
        title="Device Assignment & Provisioning"
        size="xl"
        :no-close-on-backdrop="isProvisioning"
        :no-close-on-esc="isProvisioning"
        :hide-header-close="isProvisioning"
        hide-footer
      >
        <div class="provisioning-container">
          <!-- Overall Progress -->
          <b-card class="mb-3 border-primary">
            <b-row align-v="center">
              <b-col md="8">
                <h5 class="mb-2">
                  <i class="fas fa-sync-alt fa-spin text-primary" v-if="isProvisioning"></i>
                  <i class="fas fa-check-circle text-success" v-else-if="allProvisioningComplete"></i>
                  <i class="fas fa-exclamation-triangle text-danger" v-else-if="hasProvisioningErrors"></i>
                  {{ provisioningStatusMessage }}
                </h5>
                <p class="mb-0 text-muted">
                  Assigning {{ provisioningDevices.length }} device(s) to <strong>{{ assignmentForm.assigned_to }}</strong>
                </p>
              </b-col>
              <b-col md="4" class="text-right">
                <h3 class="mb-0">{{ completedProvisioningCount }} / {{ provisioningDevices.length }}</h3>
                <small class="text-muted">Devices Completed</small>
              </b-col>
            </b-row>
            
            <!-- Overall Progress Bar -->
            <b-progress 
              :value="overallProgress" 
              :max="100" 
              height="30px"
              class="mt-3"
              :variant="overallProgressVariant"
              :animated="isProvisioning"
              :striped="isProvisioning"
            >
              <strong>{{ overallProgress }}%</strong>
            </b-progress>
          </b-card>

          <!-- Individual Device Cards -->
          <b-row>
            <b-col 
              md="6" 
              v-for="device in provisioningDevices" 
              :key="device.device_id"
              class="mb-3"
            >
              <b-card 
                :class="getDeviceCardClass(device)"
                class="device-provision-card"
              >
                <!-- Card Header -->
                <div class="d-flex align-items-center mb-3">
                  <div class="device-icon mr-3">
                    <i class="fas fa-mobile-alt fa-2x" :class="getDeviceIconClass(device)"></i>
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mb-0 font-weight-bold">{{ device.device_id }}</h6>
                    <small class="text-muted">{{ device.site || 'N/A' }} - {{ device.group || 'N/A' }}</small>
                  </div>
                  <div class="status-badge">
                    <b-badge :variant="getDeviceStatusVariant(device)" pill>
                      <i :class="getDeviceStatusIcon(device)"></i>
                      {{ device.provisionStatus }}
                    </b-badge>
                  </div>
                </div>

                <!-- Status Message -->
                <!-- In your provisioning modal template, enhance the device card -->
<!-- In your provisioning modal template - update the status message section -->
              <div class="status-message mb-2">
                <small>
                  <i :class="getDeviceStatusIcon(device)"></i>
                  <!-- ⭐⭐ SHOW ERROR MESSAGE IF EXISTS, OTHERWISE SHOW PROGRESS MESSAGE -->
                  <span v-if="device.provisionError" class="text-danger">
                    {{ device.provisionError }}
                  </span>
                  <span v-else>
                    {{ device.provisionMessage }}
                    <span v-if="device.currentFile" class="font-weight-bold"> - {{ device.currentFile }}</span>
                    <span v-if="device.filesCompleted > 0" class="text-muted">
                      ({{ device.filesCompleted }}/{{ device.filesTotal }} files)
                    </span>
                  </span>
                </small>
              </div>

                <!-- Progress Bar -->
                <b-progress 
                  :value="device.provisionProgress" 
                  :max="100"
                  height="20px"
                  :variant="getDeviceProgressVariant(device)"
                  :animated="device.provisionStatus === 'provisioning'"
                  :striped="device.provisionStatus === 'provisioning'"
                  class="mb-2"
                >
                  <span v-if="device.provisionProgress > 0">{{ device.provisionProgress }}%</span>
                </b-progress>

                <!-- File Download Status -->
                <!-- In the file download status section -->
              <div v-if="device.provisionFiles && device.provisionFiles.length > 0" class="files-status mt-3">
                <small class="text-muted font-weight-bold">
                  Files ({{ device.filesCompleted }}/{{ device.filesTotal }}):
                </small>
                <div class="file-list mt-1">
                  <div 
                    v-for="file in device.provisionFiles" 
                    :key="file.filename"
                    class="file-item d-flex align-items-center justify-content-between p-1"
                  >
                    <div class="d-flex align-items-center">
                      <i 
                        :class="getFileStatusIcon(file.status)" 
                        class="mr-2"
                        :style="{ color: getFileStatusColor(file.status) }"
                      ></i>
                      <small class="file-name">{{ file.filename }}</small>
                    </div>
                    <div class="d-flex align-items-center">
                      <small class="text-muted mr-2" v-if="file.timestamp">
                        {{ formatTimeAgo(file.timestamp) }}
                      </small>
                      <b-badge 
                        :variant="getFileStatusBadge(file.status)"
                        size="sm"
                      >
                        {{ getFileStatusText(file.status) }}
                      </b-badge>
                    </div>
                  </div>
                </div>
              </div>

                <!-- Error Message -->
                <b-alert 
                  v-if="device.provisionError" 
                  variant="danger" 
                  show 
                  class="mt-2 mb-0"
                >
                  <small>
                    <i class="fas fa-exclamation-circle"></i>
                    {{ device.provisionError }}
                  </small>
                </b-alert>
              </b-card>
            </b-col>
          </b-row>

       <!-- Action Buttons -->
     <!-- Action Buttons -->
<!-- Action Buttons -->
<div class="text-center mt-4">
  <b-button
    variant="outline-secondary"
    size="lg"
    @click="handleCloseProvisioningModal"
  >
    <i class="fas fa-times"></i> 
    {{ allProvisioningComplete ? 'Close' : 'Close Anyway' }}
  </b-button>
</div>
        </div>
      </b-modal>
    <!-- Configuration Modal -->
        <BeaconConfigModal
          :visible.sync="configModalVisible"
          :show-config-form="showConfigForm"
          :edit-mode="editMode"
          :configured-beacons="configuredBeacons"
          :loading-configs="loadingConfigs"
          :config-form="configForm"
          @add-beacon="openAddBeaconForm"
          @edit-beacon="editBeaconConfig"
          @save-beacon="saveBeaconConfig"
          @delete-beacon="deleteBeaconConfig"
          @cancel-form="cancelConfigForm"
          @add-medication="addMedication"
          @remove-medication="removeMedication"
          @reset-form="resetConfigForm"
        />
  </div>
</template>

<script>
import { Table, TableColumn } from 'element-ui';
import io from 'socket.io-client';
import ApiService from '@/services/api.service';
import Swal from 'sweetalert2';
import BeaconHeader from '@/views/components/Beacon/BeaconHeader.vue';
import StatusCard from '@/views/components/Beacon/StatusCard.vue';
import BeaconStats from '@/views/components/Beacon/BeaconStats.vue';
import LiveActivityFeed from '@/views/components/Beacon/LiveActivityFeed.vue';
import ActiveBeacons from '@/views/components/Beacon/ActiveBeacons.vue';
import ActiveDevices from '@/views/components/Beacon/ActiveDevices.vue';
import EventHistory from '@/views/components/Beacon/EventHistory.vue';
import DeviceAssignmentModal from '@/views/components/Beacon/DeviceAssignmentModal.vue';
import BeaconConfigModal from '@/views/components/Beacon/BeaconConfigModal.vue';





export default {
  name: 'BeaconDemo',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
     BeaconHeader,
    StatusCard,
    BeaconStats,
    LiveActivityFeed,
    ActiveBeacons,
    ActiveDevices,
    EventHistory,
    DeviceAssignmentModal,
    BeaconConfigModal
  },
  data() {
    return {
      socket: null,
      socketConnected: false,
      recentEvents: [],
      allEvents: [],
      activeBeacons: [],
      loading: false,
      stats: {
        totalEvents: 0,
        activeBeacons: 0,
        devicesOnline: 0,
        lastUpdate: 'Never',
        beaconsDetected: 0,
        alertsSent: 0,
        avgResponseTime: '0s'
      },
      // Configuration Modal
      configModalVisible: false,
      showConfigForm: false,
      editMode: false,
      currentBeaconId: null,
      configuredBeacons: [],
      loadingConfigs: false,
      configForm: {
        beacon_mac: '',
        patient_name: '',
        room_number: '',
        medications: [
          { name: '', dosage: '', time: '08:00' }
        ],
        rssi_threshold: -75,
        active: true,
        notes: ''
      },
      // Auto-refresh
      refreshInterval: null,
      assignmentModalVisible: false,
      showingAssignForm: false,
      assignments: [],
      availableDevices: [],
      sites: [],
      groups: [],
      loadingAssignments: false,
      selectedSite: null,
      selectedGroup: null,
      selectedDevices: [],
      assignmentForm: {
        assigned_to: '',
        notes: ''
      },
      // Live Feed Filters
      liveFilters: {
        eventType: null,
        deviceId: null,
        maxEvents: 25
      },
      // Event History Filters
      historyFilters: {
        eventType: null,
        deviceId: null,
        patient: null,
        dateRange: null
      },
      currentTime: null, // Initialize as null
    clockInterval: null,
    deviceSearchQuery: '',
    nurseAssignmentModalVisible: false,
    isAssigning: false,
    provisioningProgress: 0,
    provisioningMessage: '',
    provisioningModalVisible: false,
    isProvisioning: false,
    provisioningDevices: [],
    provisioningSessions: {},
    
    };
  },
  computed: {
    
    filteredAvailableDevices() {
      // First filter by search query
      let devices = this.availableDevices;
      
      if (this.deviceSearchQuery) {
        const query = this.deviceSearchQuery.toLowerCase();
        devices = devices.filter(device => 
          device.device_id.toLowerCase().includes(query) ||
          (device.group && device.group.toLowerCase().includes(query)) ||
          (device.site && device.site.toLowerCase().includes(query))
        );
      }
      
      // Then sort: assigned devices first, then available devices
      return [...devices].sort((a, b) => {
        // If one is assigned and other is not, assigned comes first
        if (a.is_assigned && !b.is_assigned) return -1;
        if (!a.is_assigned && b.is_assigned) return 1;
        
        // If both have same status, sort by device_id alphabetically
        return a.device_id.localeCompare(b.device_id);
      });
    },
    isConfigFormValid() {
      return (
        this.configForm.beacon_mac &&
        this.configForm.patient_name &&
        this.configForm.medications.length > 0 &&
        this.configForm.medications.every(m => m.name && m.dosage && m.time)
      );
    },
    activeDevicesList() {
      return this.assignments.filter(a => a.active !== false);
    },
    filteredLiveEvents() {
      let events = this.recentEvents;
      
      if (this.liveFilters.eventType) {
        events = events.filter(e => e.event_type === this.liveFilters.eventType);
      }
      
      if (this.liveFilters.deviceId) {
        events = events.filter(e => e.device_id === this.liveFilters.deviceId);
      }
      
      return events.slice(0, this.liveFilters.maxEvents);
    },
    hasActiveLiveFilters() {
      return this.liveFilters.eventType || this.liveFilters.deviceId;
    },
    filteredHistoryEvents() {
      let events = this.allEvents;
      
      // Filter by event type
      if (this.historyFilters.eventType) {
        events = events.filter(e => e.event_type === this.historyFilters.eventType);
      }
      
      // Filter by device
      if (this.historyFilters.deviceId) {
        events = events.filter(e => e.device_id === this.historyFilters.deviceId);
      }
      
      // Filter by patient
      if (this.historyFilters.patient) {
        events = events.filter(e => e.patient === this.historyFilters.patient);
      }
      
      // Filter by date range
      if (this.historyFilters.dateRange) {
        const days = parseInt(this.historyFilters.dateRange);
        const cutoffDate = new Date().toLocaleTimeString();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        
        events = events.filter(e => {
          const eventDate = new Date(e.timestamp).toLocaleTimeString();
          return eventDate >= cutoffDate;
        });
      }
      
      return events;
    },
    hasActiveHistoryFilters() {
      return this.historyFilters.eventType || this.historyFilters.deviceId || 
             this.historyFilters.patient || this.historyFilters.dateRange;
    },
    uniqueDevices() {
      const devices = [...new Set(this.allEvents.map(event => event.device_id))];
      return devices.filter(device => device).sort();
    },
    uniquePatients() {
      const patients = [...new Set(this.allEvents.map(event => event.patient))];
      return patients.filter(patient => patient && patient !== 'Unknown' && patient !== 'N/A').sort();
    },
    allProvisioningComplete() {
  return this.provisioningDevices.length > 0 && 
         this.provisioningDevices.every(d => 
           d.provisionStatus === 'completed' || 
           d.provisionStatus === 'failed' ||
           d.provisionStatus === 'files_sent'  // ⭐⭐ ADD THIS LINE
         );
},

hasProvisioningErrors() {
  return this.provisioningDevices.some(d => d.provisionStatus === 'failed');
},

completedProvisioningCount() {
  return this.provisioningDevices.filter(d => 
    d.provisionStatus === 'completed' || d.provisionStatus === 'failed'
  ).length;
},

overallProgress() {
  if (this.provisioningDevices.length === 0) return 0;
  const total = this.provisioningDevices.reduce((sum, d) => sum + d.provisionProgress, 0);
  return Math.round(total / this.provisioningDevices.length);
},

overallProgressVariant() {
  if (this.hasProvisioningErrors) return 'danger';
  if (this.allProvisioningComplete) return 'success';
  return 'primary';
},

provisioningStatusMessage() {
  if (this.isProvisioning) {
    return 'Provisioning in Progress...';
  } else if (this.allProvisioningComplete) {
    // *** CHANGED: More accurate status message ***
    const completedCount = this.provisioningDevices.filter(d => d.provisionStatus === 'completed').length;
    const filesSentCount = this.provisioningDevices.filter(d => d.provisionStatus === 'files_sent').length;
    
    if (completedCount === this.provisioningDevices.length) {
      return this.hasProvisioningErrors ? 'Provisioning Completed with Errors' : 'All Devices Fully Provisioned!';
    } else if (filesSentCount > 0) {
      return `${filesSentCount} device(s) waiting for reboot`;
    }
    return this.hasProvisioningErrors ? 'Provisioning Completed with Errors' : 'All Devices Provisioned Successfully!';
  }
  return 'Starting Provisioning...';
},
  },
  // In BeaconDemo.vue - watch the component's visible state

  methods: {
    

    handleComponentAssign(payload) {
    // Use the payload which contains selectedDevices and assignmentForm
    this.selectedDevices = payload.selectedDevices;
    this.assignmentForm = payload.assignmentForm;
    this.confirmAssignDevices();
  },
    // Add these helper methods
getFileStatusColor(status) {
  const colors = {
    'pending': '#6c757d',
    'downloading': '#007bff', 
    'completed': '#28a745',
    'failed': '#dc3545'
  };
  return colors[status] || '#6c757d';
},
// Add this method for manual closing
async forceCloseProvisioningModal() {
  const result = await Swal.fire({
    title: 'Close Provisioning Modal?',
    text: this.isProvisioning 
      ? 'Provisioning is still in progress. The process will continue in the background. You can check assignment status later.'
      : 'Close provisioning modal?',
    icon: this.isProvisioning ? 'warning' : 'info',
    showCancelButton: true,
    confirmButtonText: 'Yes, Close',
    cancelButtonText: 'Stay'
  });
  
  if (result.isConfirmed) {
    // Stop all polling intervals
    this.provisioningDevices.forEach(device => {
      this.stopPollingDevice(device);
    });
    
    // Force set provisioning to false
    this.isProvisioning = false;
    
    // ⭐⭐⭐ Refresh assignments before closing
    await this.loadAssignments();
    await this.loadAvailableDevices();
    
    this.closeProvisioningModal();
  }
},

getFileStatusBadge(status) {
  const variants = {
    'pending': 'secondary',
    'downloading': 'primary',
    'completed': 'success', 
    'failed': 'danger'
  };
  return variants[status] || 'secondary';
},

getFileStatusText(status) {
  const texts = {
    'pending': 'Pending',
    'downloading': 'Downloading',
    'completed': 'Completed',
    'failed': 'Failed'
  };
  return texts[status] || status;
},
   
    // WebSocket Methods
    connectWebSocket() {
      const wsUrl = 'http://infrafon.cloud:9201';
      this.socket = io(wsUrl + '/beacon-demo', {
        transports: ['websocket', 'polling']
      });

      this.socket.on('connect', () => {
       //console.log('Connected to beacon demo namespace');
        this.socketConnected = true;
      });

      this.socket.on('connection_response', (data) => {
       //console.log('Connection response:', data);
      });

      this.socket.on('beacon_event', (data) => {
       //console.log('Received beacon event:', data);
        this.handleNewEvent(data);
      });

      this.socket.on('disconnect', () => {
       //console.log('Disconnected from beacon demo');
        this.socketConnected = false;
      });
    },

    testWebSocket() {
     //console.log('=== WebSocket Debug ===');
     //console.log('Socket exists:', !!this.socket);
     //console.log('Socket connected:', this.socket?.connected);
     //console.log('Socket ID:', this.socket?.id);
     //console.log('Transport:', this.socket?.io?.engine?.transport?.name);
      
      if (this.socket) {
        this.socket.connect();
      } else {
       //console.log('Creating new socket connection...');
        this.connectWebSocket();
      }
    },

    handleNewEvent(event) {
     //console.log('Processing new event:', event);
      // *** Get current nurse assignment for this device ***
      const currentAssignment = this.assignments.find(a => a.device_id === event.device_id);
      
      const eventWithId = {
        ...event,
        id: Date.now() + Math.random(),
        timestamp: event.timestamp || new Date().toLocaleString(),
         // *** Store nurse name at time of event for historical accuracy ***
    assigned_nurse: event.nurse || 'Unassigned'  // Use the historical nurse sent from backend
      };

      this.recentEvents.unshift(eventWithId);
      if (this.recentEvents.length > 50) {
        this.recentEvents.pop();
      }

      this.allEvents.unshift(eventWithId);

      if (event.event_type === 'beacon_detected') {
        this.updateActiveBeacons(eventWithId);
      }
      
      if (event.event_type === 'confirm_visit') {
        this.handleConfirmVisit(eventWithId);
      }
      
      this.updateActiveDevices(eventWithId);
      this.updateStats();
      
   
    },

    handleConfirmVisit(event) {
     //console.log('Handling confirm visit event:', event);
      
      this.$notify({
        title: 'Visit Confirmed',
        message: `Device ${event.device_id} confirmed the visit`,
        type: 'success',
        duration: 5000
      });
      
      const relatedEvent = this.allEvents.find(e => 
        e.device_id === event.device_id && 
        e.event_type === 'beacon_detected' &&
        Math.abs(new Date(e.timestamp).getTime() - new Date(event.timestamp).toLocaleTimeString().getTime()) < 5 * 60 * 1000
      );
      
      if (relatedEvent) {
        this.$set(relatedEvent, 'confirmed', true);
        this.$set(relatedEvent, 'confirmed_at', event.timestamp);
       //console.log('Updated related beacon event as confirmed');
      }
    },

    updateActiveDevices(event) {
  if (event.device_id) {
    const existingDevice = this.assignments.find(a => a.device_id === event.device_id);
    
    if (existingDevice) {
      const newActivityTime = new Date().toISOString();
     //console.log(`Updating device ${event.device_id} activity from ${existingDevice.last_activity} to ${newActivityTime}`);
      
      this.$set(existingDevice, 'last_activity', newActivityTime);
     //console.log(`Updated device ${event.device_id} activity`);
      
      // Test the formatTimeAgo calculation
     //console.log(`Time ago calculation: ${this.formatTimeAgo(newActivityTime)}`);
    } else {
     //console.log(`Device ${event.device_id} not found in assignments`);
      this.loadAssignments();
    }
  }
},

    updateActiveBeacons(event) {
      if (event.event_type === 'beacon_detected' && event.beacon_mac) {
        const existingIndex = this.activeBeacons.findIndex(b => b.mac === event.beacon_mac);
        const beaconData = {
          mac: event.beacon_mac,
          patient: event.patient || 'Unknown Patient',
          room: event.room || 'N/A',
          rssi: event.rssi || -100,
          lastSeen: new Date().toISOString()
        };

        if (existingIndex >= 0) {
          this.$set(this.activeBeacons, existingIndex, beaconData);
        } else {
          this.activeBeacons.push(beaconData);
        }
        
         const oneHourAgo = Date.now() - 60 * 60 * 1000;
    this.activeBeacons = this.activeBeacons.filter(
      b => new Date(b.lastSeen).getTime() > oneHourAgo // ← Now this will work correctly
    );
    

        
      }
    },

    clearEvents() {
      this.recentEvents = [];
      this.$notify({
        title: 'Cleared',
        message: 'Recent events cleared',
        type: 'info'
      });
    },

    formatTimeAgo(dateString) {
          if (!dateString) {
            return "N/A";
          }
          //console.log(dateString);
          const date = new Date(dateString);
          const seconds = Math.floor((Date.now() - date) / 1000);

          // Define time intervals in seconds
          const intervals = {
            year: 31536000,
            month: 2592000,
            week: 604800,
            day: 86400,
            hour: 3600,
            minute: 60,
          };

          if (seconds < 5) {
            return "just now";
          } else if (seconds < intervals.minute) {
            return `${seconds} seconds ago`;
          } else if (seconds < intervals.hour) {
            const minutes = Math.floor(seconds / intervals.minute);
            return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
          } else if (seconds < intervals.day) {
            const hours = Math.floor(seconds / intervals.hour);
            return `${hours} hour${hours > 1 ? "s" : ""} ago`;
          } else if (seconds < intervals.week) {
            const days = Math.floor(seconds / intervals.day);
            return `${days} day${days > 1 ? "s" : ""} ago`;
          } else if (seconds < intervals.month) {
            const weeks = Math.floor(seconds / intervals.week);
            return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
          } else if (seconds < intervals.year) {
            const months = Math.floor(seconds / intervals.month);
            return `${months} month${months > 1 ? "s" : ""} ago`;
          } else {
            const years = Math.floor(seconds / intervals.year);
            return `${years} year${years > 1 ? "s" : ""} ago`;
          }
        },

    updateClock() {
    const now = new Date();
    const formattedTime = this.formatTime(now);
    //console.log('Current time:', now);
    //console.log('Formatted time:', formattedTime);
    this.currentTime = formattedTime;
  },
    formatTime(timestamp) {
      if (!timestamp) {
         //console.log('No timestamp provided');
          return 'N/A';
        }  
      const date = new Date(timestamp);
     //console.log("date:",date)
     //console.log("timestamp:",timestamp)
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      // Use German locale with local timezone
      return date.toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'Europe/Berlin'  // Force German timezone
      });
    },

    updateStats() {
      this.stats.totalEvents = this.allEvents.length;
      this.stats.activeBeacons = this.activeBeacons.length;
      this.stats.devicesOnline = this.activeDevicesList.length;
      this.stats.lastUpdate = new Date().toLocaleTimeString();
      
      const last24h = Date.now() - 24 * 60 * 60 * 1000;
      const recent = this.allEvents.filter(e => 
        new Date(e.timestamp).getTime() > last24h
      );
      
      this.stats.beaconsDetected = recent.filter(
        e => e.event_type === 'beacon_detected'
      ).length;
      
      this.stats.alertsSent = recent.filter(
        e => e.message_sent
      ).length;
      
      this.stats.visitsConfirmed = recent.filter(
        e => e.event_type === 'confirm_visit'
      ).length;
    },

  async loadEventHistory() {
  this.loading = true;
  try {
    const response = await ApiService.getT('beacon-events', {
      params: { limit: 100 }
    });
    
    // ⭐⭐ FIX: Use the historical nurse from backend, don't override with current assignments
    this.allEvents = response.data.data.events.map(event => ({
      ...event,
      // ⭐⭐ Use the historical nurse name that was stored when the event occurred
      assigned_nurse: event.nurse || 'Unassigned'  // 'nurse' comes from backend, 'assigned_nurse' is frontend field
    }));
    
    this.recentEvents = this.allEvents.slice(0, 50);
    
    this.allEvents.forEach(event => {
      if (event.event_type === 'beacon_detected') {
        this.updateActiveBeacons(event);
      }
    });
    
    this.updateStats();
  } catch (error) {
    console.error('Failed to load event history:', error);
    this.$notify({
      title: 'Error',
      message: 'Failed to load event history',
      type: 'error'
    });
  } finally {
    this.loading = false;
  }
},

    formatTime(timestamp) {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  
  const formattedDate = date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Europe/Berlin'
  });
  
  // Add timezone abbreviation
  const timeZone = date.toLocaleTimeString('en-US', {
    timeZoneName: 'short',
    timeZone: 'Europe/Berlin'
  }).split(' ').pop();
  
  return `${formattedDate} ${timeZone}`;
},

    // Live Feed Filter Methods
    applyLiveFilters() {
     //console.log('Live filters applied:', this.liveFilters);
    },
    
    clearLiveFilter(filterName) {
      this.liveFilters[filterName] = null;
    },
    
    clearAllLiveFilters() {
      this.liveFilters = {
        eventType: null,
        deviceId: null,
        maxEvents: 25
      };
    },
    
    getDeviceName(deviceId) {
      const device = this.activeDevicesList.find(d => d.device_id === deviceId);
      return device ? device.assigned_to : deviceId;
    },
    
    getEventTypeCount(eventType) {
      return this.recentEvents.filter(e => e.event_type === eventType).length;
    },

    // Event History Filter Methods
    applyHistoryFilters() {
     //console.log('History filters applied:', this.historyFilters);
    },
    
    clearHistoryFilter(filterName) {
      this.historyFilters[filterName] = null;
    },
    
    clearAllHistoryFilters() {
      this.historyFilters = {
        eventType: null,
        deviceId: null,
        patient: null,
        dateRange: null
      };
    },
    
    getDateRangeLabel(days) {
      const labels = {
        '1': 'Last 24 Hours',
        '7': 'Last 7 Days',
        '30': 'Last 30 Days'
      };
      return labels[days] || `${days} Days`;
    },

    exportEventHistory() {
      // Simple CSV export implementation
      const headers = ['Time', 'Event Type', 'Patient', 'Nurse', 'Device', 'Location', 'Beacon MAC', 'RSSI', 'Medications'];
      const csvData = this.filteredHistoryEvents.map(event => [
        this.formatTime(event.timestamp),
        this.getEventTypeLabel(event.event_type),
        event.patient || 'Unknown',
        this.getNurseName(event.device_id),
        event.device_id,
        event.room || 'N/A',
        event.beacon_mac || 'N/A',
        event.rssi || 'N/A',
        event.medications ? event.medications.join('; ') : 'None'
      ]);
      
      const csvContent = [headers, ...csvData]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `event-history-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      this.$notify({
        title: 'Export Complete',
        message: `Exported ${this.filteredHistoryEvents.length} events`,
        type: 'success'
      });
    },

    getEventVariant(event) {
      if (event.event_type === 'confirm_visit') return 'success';
      if (event.event_type === 'beacon_detected') {
        return event.confirmed ? 'success' : 'info';
      }
      return 'primary';
    },
    
    getEventIcon(event) {
      const icons = {
        beacon_detected: 'fas fa-broadcast-tower',
        confirm_visit: 'fas fa-check-circle',
        message_sent: 'fas fa-paper-plane'
      };
      return icons[event.event_type] || 'fas fa-circle';
    },

    getEventTitle(event) {
      if (event.event_type === 'confirm_visit') {
        return `Visit Confirmed`;
      } else if (event.event_type === 'beacon_detected') {
        return `Beacon Detected`;
      }
      return 'Event';
    },

    getRssiClass(rssi) {
      if (!rssi) return '';
      if (rssi > -60) return 'text-success';
      if (rssi > -70) return 'text-warning';
      return 'text-danger';
    },

    // Configuration Modal Methods
    openConfigModal() {
      this.configModalVisible = true;
      this.loadConfiguredBeacons();
    },

    async loadConfiguredBeacons() {
      this.loadingConfigs = true;
      try {
        const response = await ApiService.getT('beacon-configs');
        this.configuredBeacons = response.data.data.beacons;
      } catch (error) {
        console.error('Failed to load beacon configs:', error);
        this.$notify({
          title: 'Error',
          message: 'Failed to load beacon configurations',
          type: 'error'
        });
      } finally {
        this.loadingConfigs = false;
      }
    },

    openAddBeaconForm() {
      this.showConfigForm = true;
      this.editMode = false;
      this.resetConfigForm();
    },

    editBeaconConfig(beacon) {
      this.showConfigForm = true;
      this.editMode = true;
      this.currentBeaconId = beacon.id;
      this.configForm = {
        beacon_mac: beacon.beacon_mac,
        patient_name: beacon.patient_name,
        room_number: beacon.room_number || '',
        medications: beacon.medications.map(m => ({
          name: m.name,
          dosage: m.dosage,
          time: m.time
        })),
        rssi_threshold: beacon.rssi_threshold,
        active: beacon.active,
        notes: beacon.notes || ''
      };
    },

    async saveBeaconConfig() {
      try {
        if (this.editMode) {
          await ApiService.putT(`beacon-configs/${this.currentBeaconId}`, this.configForm);
          this.$notify({
            title: 'Success',
            message: 'Beacon configuration updated',
            type: 'success'
          });
        } else {
          await ApiService.postT('beacon-configs', this.configForm);
          this.$notify({
            title: 'Success',
            message: 'Beacon configuration created',
            type: 'success'
          });
        }
        this.showConfigForm = false;
        await this.loadConfiguredBeacons();
      } catch (error) {
        console.error('Failed to save beacon:', error);
        this.$notify({
          title: 'Error',
          message: error.errorInfo?.userMessage || 'Failed to save beacon configuration',
          type: 'error'
        });
      }
    },

    async deleteBeaconConfig(beacon) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Delete beacon configuration for ${beacon.patient_name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        try {
          await ApiService.deleteT(`beacon-configs/${beacon.id}`);
          this.$notify({
            title: 'Success',
            message: 'Beacon configuration deleted',
            type: 'success'
          });
          await this.loadConfiguredBeacons();
        } catch (error) {
          console.error('Failed to delete beacon:', error);
          this.$notify({
            title: 'Error',
            message: 'Failed to delete beacon configuration',
            type: 'error'
          });
        }
      }
    },

    addMedication() {
      if (this.configForm.medications.length < 5) {
        this.configForm.medications.push({
          name: '',
          dosage: '',
          time: '08:00'
        });
      }
    },

    removeMedication(index) {
      if (this.configForm.medications.length > 1) {
        this.configForm.medications.splice(index, 1);
      }
    },

    cancelConfigForm() {
      this.showConfigForm = false;
      this.resetConfigForm();
    },

    resetConfigForm() {
      this.configForm = {
        beacon_mac: '',
        patient_name: '',
        room_number: '',
        medications: [
          { name: '', dosage: '', time: '08:00' }
        ],
        rssi_threshold: -75,
        active: true,
        notes: ''
      };
      this.currentBeaconId = null;
      this.editMode = false;
    },

    // Device Assignment Methods
    async openAssignmentModal() {
      this.assignmentModalVisible = true;
      await this.loadAssignments();
      await this.loadAvailableDevices();
      await this.loadSites();
      await this.loadGroups();
    },

    async loadAssignments() {
      this.loadingAssignments = true;
      try {
        const response = await ApiService.getT('device-assignments', {
          params: { active_only: true }
        });
        this.assignments = response.data.data.assignments;
      } catch (error) {
        console.error('Failed to load assignments:', error);
        this.$notify({
          title: 'Error',
          message: 'Failed to load device assignments',
          type: 'error'
        });
      } finally {
        this.loadingAssignments = false;
      }
    },

    async loadAvailableDevices() {
      this.loadingAssignments = true;
      try {
        const params = {};
        if (this.selectedSite) params.site = this.selectedSite;
        if (this.selectedGroup) params.group = this.selectedGroup;
        
        const response = await ApiService.getT('device-assignments/available-devices', {
          params: params
        });
        this.availableDevices = response.data.data.devices;
      } catch (error) {
        console.error('Failed to load devices:', error);
        this.$notify({
          title: 'Error',
          message: 'Failed to load available devices',
          type: 'error'
        });
      } finally {
        this.loadingAssignments = false;
      }
    },

    async loadSites() {
      try {
        const response = await ApiService.getT('device-assignments/sites');
        this.sites = response.data.data.sites;
      } catch (error) {
        console.error('Failed to load sites:', error);
      }
    },

    async loadGroups() {
      try {
        const params = {};
        if (this.selectedSite) params.site = this.selectedSite;
        
        const response = await ApiService.getT('device-assignments/groups', {
          params: params
        });
        this.groups = response.data.data.groups;
      } catch (error) {
        console.error('Failed to load groups:', error);
      }
    },

    filterDevicesBySite() {
      this.selectedGroup = null;
      this.loadAvailableDevices();
      this.loadGroups();
    },

    filterDevicesByGroup() {
      this.loadAvailableDevices();
    },

    handleDeviceSelection(selection) {
      this.selectedDevices = selection.filter(d => !d.is_assigned);
    },

    openNurseAssignmentModal() {
  if (this.selectedDevices.length === 0) {
    this.$notify({
      title: 'Warning',
      message: 'Please select at least one device',
      type: 'warning'
    });
    return;
  }
  this.nurseAssignmentModalVisible = true;
},

    cancelAssignForm() {
      this.showingAssignForm = false;
      this.assignmentForm = {
        device_id: '',
        assigned_to: '',
        device_name: '',
        notes: ''
      };
    },

async confirmAssignDevices() {
  if (this.selectedDevices.length === 0) {
    this.$notify({
      title: 'Warning',
      message: 'Please select at least one device',
      type: 'warning'
    });
    return;
  }

  // Initialize devices for polling-based tracking
  this.provisioningDevices = this.selectedDevices.map(device => ({
    ...device,
    provisionStatus: 'waiting',
    provisionProgress: 0,
    provisionMessage: 'Starting assignment...',
    provisionError: null,
    currentFile: null,
    filesCompleted: 0,
    filesTotal: 0,
    provisionFiles: [],
    pollingInterval: null
  }));

  // Show provisioning modal
  this.provisioningModalVisible = true;
  this.isProvisioning = true;
  this.nurseAssignmentModalVisible = false;

  try {
    // Create assignments and start polling for each device
    for (let device of this.provisioningDevices) {
      try {
        device.provisionStatus = 'assigning';
        device.provisionProgress = 10;
        device.provisionMessage = 'Creating assignment...';

        // Create assignment
        const response = await ApiService.postT('device-assignments', {
          device_id: device.device_id,
          device_name: device.device_id,
          assigned_to: this.assignmentForm.assigned_to,
          site: device.site,
          group: device.group,
          notes: this.assignmentForm.notes
        });

        device.provisionProgress = 20;
        device.provisionMessage = 'Assignment created - starting file downloads...';
        
       //console.log(`✅ Assignment created for ${device.device_id}, starting polling...`);

        // Start polling for this device
        this.startPollingDeviceStatus(device);
        
      } catch (error) {
  console.error(`❌ Failed to assign device ${device.device_id}:`, error);
  
  let errorMessage = 'Assignment failed';
  
  if (error.errorInfo) {
    // ⭐⭐ FIX: Use the correct property name with space
    if (error.errorInfo['developer message']) {
      errorMessage = error.errorInfo['developer message'];
     //console.log('✅ Using developer message (with space):', errorMessage);
    } else if (error.errorInfo.userMessage) {
      errorMessage = error.errorInfo.userMessage;
     //console.log('✅ Using userMessage:', errorMessage);
    }
  } else if (error.response && error.response.data) {
    // Fallback to response data
    if (error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.data.error) {
      errorMessage = error.response.data.error;
    }
  } else if (error.message) {
    errorMessage = error.message;
  }
  
  device.provisionStatus = 'failed';
  device.provisionProgress = 0;
  device.provisionMessage = '';
  device.provisionError = errorMessage;
}
    }

  } finally {
    // Clear form only if we're not continuing with provisioning
    if (this.provisioningDevices.every(d => d.provisionStatus === 'failed')) {
      this.assignmentForm.assigned_to = '';
      this.assignmentForm.notes = '';
      this.selectedDevices = [];
    }
  }
},
// Add this method to handle API polling for device status
// In your BeaconDemo.vue, update the polling method:
// Replace the startPollingDeviceStatus method with this:
startPollingDeviceStatus(device) {
  // Clear any existing interval first
  this.stopPollingDevice(device);

  let pollCount = 0;
  const maxPollCount = 180;
  
 //console.log(`🚀 Starting polling for device ${device.device_id}`);
  
  // ⭐⭐⭐ FIX: Use Vue.set or direct assignment with proper reactivity
  const intervalId = setInterval(async () => {
    try {
      pollCount++;
     //console.log(`🔄 Polling device ${device.device_id} (attempt ${pollCount}), interval: ${intervalId}`);
      
      const response = await ApiService.getT(`device-assignments/status/${device.device_id}`, {
        params: { 
          timestamp: Date.now()
        }
      });
      
      if (!response.data || !response.data.data) {
        console.warn(`⚠️ No data in response for ${device.device_id}`);
        return;
      }
      
      const statusData = response.data.data;
      
      // Update device progress based on API response
      this.updateDeviceFromApi(device, statusData);
      
      // Check if we should stop polling
      if (this.shouldStopPolling(device, statusData) || pollCount >= maxPollCount) {
       //console.log(`🛑 Stopping polling for ${device.device_id}, interval: ${intervalId}`);
        this.stopPollingDevice(device);
        
        if (pollCount >= maxPollCount) {
          device.provisionStatus = 'failed';
          device.provisionError = 'Polling timeout - device did not complete within 3 minutes';
          console.error(`⏰ Timeout for device ${device.device_id}`);
        }
      }
      
    } catch (error) {
      console.error(`❌ Error polling device ${device.device_id}:`, error);
      
      if (pollCount >= maxPollCount) {
        this.stopPollingDevice(device);
        device.provisionStatus = 'failed';
        device.provisionError = 'Failed to get device status: ' + (error.message || 'Unknown error');
      }
    }
  }, 2000); // Poll every 2 seconds
  
  // ⭐⭐⭐ FIX: Set the interval ID directly on the device object
  device.pollingInterval = intervalId;
 //console.log(`✅ Polling started for ${device.device_id}, interval ID: ${intervalId}`);
},
// Add this computed property
getCloseButtonText() {
  return this.allProvisioningComplete ? 'Close' : 'Close Anyway';
},

// Replace the forceCloseProvisioningModal method with this:
async handleCloseProvisioningModal() {
  if (this.allProvisioningComplete) {
    // If complete, just close without confirmation
    this.closeProvisioningModal();
  } else {
    // If not complete, show confirmation
    const result = await Swal.fire({
      title: 'Close Provisioning Modal?',
      text: 'Provisioning is still in progress. The process will continue in the background. You can check assignment status later.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Close',
      cancelButtonText: 'Stay'
    });
    
    if (result.isConfirmed) {
      // Stop all polling intervals
      this.provisioningDevices.forEach(device => {
        this.stopPollingDevice(device);
      });
      
      // Force set provisioning to false
      this.isProvisioning = false;
      
      // Refresh assignments before closing
      await this.loadAssignments();
      await this.loadAvailableDevices();
      
      this.closeProvisioningModal();
    }
  }
},
// Helper method to update device from API response
// Helper method to update device from API response
updateDeviceFromApi(device, statusData) {
 //console.log('📡 POLLING RESPONSE:', statusData);
  
  // Update basic status
  device.provisionStatus = statusData.overall_status || device.provisionStatus;
  device.provisionMessage = statusData.message || device.provisionMessage;
  device.provisionProgress = statusData.progress || device.provisionProgress;
  
  if (statusData.overall_status === 'completed') {
   //console.log(`🎉 DETECTED COMPLETED STATUS for ${device.device_id} - stopping polling soon`);
  }

  // ⭐⭐⭐ FIXED: Proper file status handling
  if (statusData.file_status && Array.isArray(statusData.file_status)) {
   //console.log('📁 Processing file status from API:', statusData.file_status);
    
    // Update file status array
    device.provisionFiles = statusData.file_status.map(file => ({
      filename: file.filename,
      status: file.status || 'pending',
      timestamp: file.timestamp || new Date().toISOString()
    }));
    
    // Update file counts from the array
    device.filesTotal = statusData.files_total || device.provisionFiles.length;
    device.filesCompleted = device.provisionFiles.filter(f => 
      f.status === 'completed' || f.status === 'downloaded'
    ).length;
    
   //console.log(`📊 File status updated: ${device.filesCompleted}/${device.filesTotal} completed`);
  } else {
    // Fallback: use top-level fields if file_status array is not provided
    if (statusData.files_completed !== undefined) {
      device.filesCompleted = statusData.files_completed;
    }
    if (statusData.files_total !== undefined) {
      device.filesTotal = statusData.files_total;
    }
  }
  
  // ⭐⭐⭐ FIXED: Handle completion status - REMOVE THE STOP POLLING CALLS
  if (statusData.overall_status === 'completed') {
    device.provisionStatus = 'completed';
    device.provisionMessage = statusData.message || 'Device fully provisioned and rebooted!';
    device.provisionProgress = 100;
    
    // Ensure all files show as completed
    if (device.provisionFiles && device.provisionFiles.length > 0) {
      device.provisionFiles.forEach(file => {
        if (file.status !== 'completed') {
          file.status = 'completed';
        }
      });
      device.filesCompleted = device.filesTotal;
    }
    
    // ⭐⭐ REMOVED: Don't stop polling here - let shouldStopPolling handle it
    // this.stopPollingDevice(device);
    
    // ⭐⭐ REMOVED: Don't check all devices here - let the polling loop handle it
    // this.checkAllDevicesCompleted();
  } else if (statusData.overall_status === 'rebooting') {
    device.provisionStatus = 'rebooting';
    device.provisionMessage = 'Device is rebooting...';
    device.provisionProgress = 90;
  } else if (statusData.overall_status === 'downloading') {
    device.provisionStatus = 'downloading';
    device.provisionMessage = `Downloading files... (${device.filesCompleted}/${device.filesTotal})`;
  }

 //console.log(`📊 Device ${device.device_id} status: ${device.provisionStatus}, progress: ${device.provisionProgress}%, files: ${device.filesCompleted}/${device.filesTotal}`);
},

// ⭐⭐⭐ ADD THIS NEW METHOD
// Replace the checkAllDevicesCompleted method with this:
// Update the checkAllDevicesCompleted method:
checkAllDevicesCompleted() {
  if (this.provisioningDevices.length === 0) {
   //console.log('No provisioning devices to check');
    return;
  }
  
  const allCompleted = this.provisioningDevices.every(device => 
    device.provisionStatus === 'completed' || 
    device.provisionStatus === 'failed' ||
    device.provisionStatus === 'files_sent'  // ⭐⭐ ADD THIS LINE
  );
  
 //console.log(`🔍 Checking completion: ${allCompleted}, devices: ${this.provisioningDevices.length}`);
  
  if (allCompleted) {
   //console.log('✅ All devices completed provisioning, stopping provisioning status');
    this.isProvisioning = false;
    
    // ⭐⭐⭐ FORCE STOP ALL POLLING
    this.provisioningDevices.forEach(device => {
      this.stopPollingDevice(device);
    });
    
    // ⭐⭐⭐ FORCE UI UPDATE
    this.$forceUpdate();
    
   //console.log('🎯 Final state - isProvisioning:', this.isProvisioning);
   //console.log('🎯 Final state - allProvisioningComplete:', this.allProvisioningComplete);
  }
},
// Replace the shouldStopPolling method with this:
// Replace the shouldStopPolling method with this:
shouldStopPolling(device, statusData) {
  // ⭐⭐ ADD 'files_sent' to completed statuses
  const completedStatuses = ['completed', 'failed', 'error', 'files_sent'];
  const shouldStop = completedStatuses.includes(statusData.overall_status);
  
  if (shouldStop) {
   //console.log(`🛑 Should stop polling for ${device.device_id} - status: ${statusData.overall_status}`);
    
    // ⭐⭐ IMPORTANT: Update the device status immediately
    device.provisionStatus = statusData.overall_status;
    device.provisionProgress = statusData.progress || 100;
    device.provisionMessage = statusData.message || 'Provisioning completed';
    
    // Force UI update
    this.$forceUpdate();
    
    // Check if all devices are complete
    setTimeout(() => {
      this.checkAllDevicesCompleted();
    }, 100);
  }
  
  return shouldStop;
},

// Helper method to stop polling for a device
// Make sure this method properly stops polling
// Replace the stopPollingDevice method with this:
stopPollingDevice(device) {
  if (device.pollingInterval) {
   //console.log(`🛑 Clearing interval for device ${device.device_id}, ID: ${device.pollingInterval}`);
    clearInterval(device.pollingInterval);
    device.pollingInterval = null;
  } else {
   //console.log(`⚠️ No polling interval found for device ${device.device_id}, but checking active intervals...`);
    
    // ⭐⭐⭐ FALLBACK: Try to find and clear any active interval
    const intervals = Object.values(this.provisioningDevices)
      .filter(d => d.pollingInterval)
      .map(d => d.pollingInterval);
    
   //console.log(`Active intervals found: ${intervals.length}`);
  }
},

async pollAssignmentStatus(device) {
  const maxAttempts = 60; // 2 minutes maximum
  let attempts = 0;

  const pollInterval = setInterval(async () => {
    try {
      attempts++;
      
      // Poll the assignment status
      const statusResponse = await ApiService.getT('device-assignments', {
        params: { device_id: device.device_id }
      });
      
      const assignments = statusResponse.data.data.assignments || [];
      const currentAssignment = assignments.find(a => a.device_id === device.device_id);
      
      if (currentAssignment) {
        // Check file download status
        if (currentAssignment.file_download_status) {
          const files = Object.values(currentAssignment.file_download_status);
          const totalFiles = files.length;
          const completedFiles = files.filter(f => f.status === 'completed').length;
          
          device.provisionProgress = 40 + (completedFiles / totalFiles) * 50; // 40-90% range
          device.provisionMessage = `Downloading files... (${completedFiles}/${totalFiles})`;
          
          // Update individual file status for display
          device.provisionFiles = files.map(file => ({
            filename: file.filename,
            status: file.status
          }));
          
         //console.log(`📁 Device ${device.device_id}: ${completedFiles}/${totalFiles} files downloaded`);
        }
        
        // Check if assignment is completed
        if (currentAssignment.assignment_completed) {
          clearInterval(pollInterval);
          
          if (currentAssignment.reboot_sent) {
            device.provisionStatus = 'completed';
            device.provisionMessage = 'Device rebooted - assignment active!';
            device.provisionProgress = 100;
           //console.log(`🎉 Device ${device.device_id} assignment completed with reboot`);
          } else {
            device.provisionStatus = 'files_sent';
            device.provisionMessage = 'Files downloaded - waiting for device reboot...';
            device.provisionProgress = 90;
           //console.log(`📁 Device ${device.device_id} files downloaded, waiting for reboot`);
          }
        }
      }
      
      // Timeout or completion check
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        device.provisionStatus = 'failed';
        device.provisionError = 'Assignment timeout - device did not complete download within 2 minutes';
        console.error(`⏰ Device ${device.device_id} assignment timeout`);
      }
      
    } catch (error) {
      console.error(`❌ Error polling device ${device.device_id}:`, error);
      
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        device.provisionStatus = 'failed';
        device.provisionError = 'Failed to monitor assignment status';
      }
    }
  }, 2000); // Poll every 2 seconds
},
closeProvisioningModal() {
  // First, stop ALL polling
  this.provisioningDevices.forEach(device => {
    this.stopPollingDevice(device);
  });
  
  // Set provisioning to false
  this.isProvisioning = false;
  
  // Close modal
  this.provisioningModalVisible = false;
  
  // Refresh data after a short delay
  setTimeout(() => {
    this.loadAssignments();
    this.loadAvailableDevices();
  }, 1000);
  
 //console.log('✅ Provisioning modal closed, assignments refreshed');
},
    getDeviceCardClass(device) {
      return {
        'border-success': device.provisionStatus === 'completed',
        'border-danger': device.provisionStatus === 'failed',
        'border-primary': device.provisionStatus === 'provisioning',
        'shadow-sm': true
      };
    },

    getDeviceIconClass(device) {
      if (device.provisionStatus === 'completed') return 'text-success';
      if (device.provisionStatus === 'failed') return 'text-danger';
      if (device.provisionStatus === 'provisioning') return 'text-primary';
      return 'text-muted';
    },

   getDeviceStatusVariant(device) {
  const statusMap = {
    'waiting': 'secondary',
    'assigning': 'info',
    'starting': 'primary',
    'sending': 'primary',
    'sent': 'primary',
    'downloading': 'primary',
    'completed': 'success',
    'rebooting': 'warning',
    'partial_complete': 'warning',
    'failed': 'danger',
    'error': 'danger',
    'timeout': 'danger'
  };
  return statusMap[device.provisionStatus] || 'secondary';
},

getDeviceStatusIcon(device) {
  const iconMap = {
    'waiting': 'fas fa-clock',
    'assigning': 'fas fa-user-plus',
    'starting': 'fas fa-play-circle',
    'sending': 'fas fa-paper-plane',
    'sent': 'fas fa-check',
    'downloading': 'fas fa-download',
    'completed': 'fas fa-check-circle',
    'rebooting': 'fas fa-sync-alt',
    'partial_complete': 'fas fa-exclamation-circle',
    'failed': 'fas fa-times-circle',
    'error': 'fas fa-exclamation-triangle',
    'timeout': 'fas fa-hourglass-end'
  };
  return iconMap[device.provisionStatus] || 'fas fa-question';
},

getDeviceProgressVariant(device) {
  if (device.provisionStatus === 'failed' || device.provisionStatus === 'error' || device.provisionStatus === 'timeout') {
    return 'danger';
  }
  if (device.provisionStatus === 'completed') {
    return 'success';
  }
  if (device.provisionStatus === 'rebooting' || device.provisionStatus === 'partial_complete') {
    return 'warning';
  }
  return 'primary';
},
   

    getFileStatusIcon(status) {
      const iconMap = {
        'pending': 'fas fa-clock text-muted',
        'downloading': 'fas fa-spinner fa-spin text-primary',
        'completed': 'fas fa-check-circle text-success',
        'failed': 'fas fa-times-circle text-danger'
      };
      return iconMap[status] || 'fas fa-question text-muted';
    },
    async waitForProvisioning(deviceId, sessionId) {
      return new Promise((resolve, reject) => {
        // Create SSE connection to monitor provisioning
        const eventSource = new EventSource(
          `/ife/rest/v1/provision/${deviceId}/stream`
        );

        const timeout = setTimeout(() => {
          eventSource.close();
          reject(new Error('Provisioning timeout'));
        }, 180000); // 3 minute timeout

        eventSource.addEventListener('provision_completed', (event) => {
          clearTimeout(timeout);
          eventSource.close();
          resolve();
        });

        eventSource.addEventListener('provision_failed', (event) => {
          clearTimeout(timeout);
          eventSource.close();
          const data = JSON.parse(event.data);
          reject(new Error(data.error_message || 'Provisioning failed'));
        });

        eventSource.onerror = (error) => {
          clearTimeout(timeout);
          eventSource.close();
          reject(new Error('Connection to provisioning stream lost'));
        };
      });
    },

    async unassignDevice(deviceId) {
      try {
        const assignment = this.assignments.find(a => a.device_id === deviceId);
        if (!assignment) {
          this.$notify({
            title: 'Error',
            message: 'Assignment not found',
            type: 'error'
          });
          return;
        }

        await ApiService.deleteT(`device-assignments/${assignment.id}`);
        
        this.$notify({
          title: 'Success',
          message: `Device ${deviceId} unassigned`,
          type: 'success'
        });

        await this.loadAssignments();
        await this.loadAvailableDevices();
        
      } catch (error) {
        console.error('Failed to unassign device:', error);
        this.$notify({
          title: 'Error',
          message: 'Failed to unassign device',
          type: 'error'
        });
      }
    },

    async deleteAssignment(assignmentId) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This will unassign the device from the nurse',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, unassign it!'
      });

      if (result.isConfirmed) {
        try {
          await ApiService.deleteT(`device-assignments/${assignmentId}`);
          
          this.$notify({
            title: 'Success',
            message: 'Assignment deleted',
            type: 'success'
          });

          await this.loadAssignments();
          await this.loadAvailableDevices();
          
        } catch (error) {
          console.error('Failed to delete assignment:', error);
          this.$notify({
            title: 'Error',
            message: 'Failed to delete assignment',
            type: 'error'
          });
        }
      }
    },

    getStateVariant(state) {
      switch (state) {
        case 'ALLOCATED':
          return 'success';
        case 'PROVISIONING':
        case 'DFU':
          return 'primary';
        case 'ERROR':
        case 'DEFECT':
        case 'UNAVAILABLE':
          return 'danger';
        case 'BOOTING':
        case 'WAITING':
          return 'warning';
        default:
          return 'secondary';
      }
    },

    getNurseName(deviceId) {
  // *** First check if we have historical data in recent events ***
  const recentEvent = this.recentEvents.find(e => e.device_id === deviceId);
  if (recentEvent && recentEvent.assigned_nurse) {
    return recentEvent.assigned_nurse;
  }
  
  // *** Fallback to current assignments ***
  const assignment = this.assignments.find(a => a.device_id === deviceId);
  return assignment ? assignment.assigned_to : deviceId;
},
    
    getRssiBadgeVariant(rssi) {
      if (rssi > -60) return 'success';
      if (rssi > -70) return 'warning';
      return 'danger';
    },
    
    getEventTypeLabel(eventType) {
      const labels = {
        beacon_detected: 'Detection',
        confirm_visit: 'Confirmed',
        message_sent: 'Message'
      };
      return labels[eventType] || eventType;
    },
    
    getEventTypeBadgeVariant(eventType) {
      const variants = {
        beacon_detected: 'primary',
        confirm_visit: 'success',
        message_sent: 'info'
      };
      return variants[eventType] || 'secondary';
    },
    // Add this with your other methods
setupAssignmentProgressListener() {
  if (this.socket) {
    this.socket.on('assignment_progress', (data) => {
     //console.log('📡 REAL-TIME ASSIGNMENT UPDATE:', data);
      
      // Find the device in provisioning devices
      const device = this.provisioningDevices.find(d => d.device_id === data.device_id);
      if (device) {
        this.updateDeviceProgress(device, data);
      }
    });
    
    //console.log('🎧 Real-time assignment progress listener activated');
  } else {
    console.warn('⚠️ Socket not available for assignment tracking');
  }
},

updateDeviceProgress(device, data) {
  // Update device progress based on real-time data
  device.provisionStatus = data.status;
  device.provisionMessage = data.message;
  device.provisionProgress = data.progress;
  
  // Update file-specific information if available
  if (data.current_file) {
    device.currentFile = data.current_file;
  }
  
  if (data.files_completed !== undefined && data.files_total !== undefined) {
    device.filesCompleted = data.files_completed;
    device.filesTotal = data.files_total;
  }
  
 //console.log(`📊 Device ${device.device_id} updated: ${data.status} - ${data.progress}%`);
},
  },
  mounted() {
    this.connectWebSocket();
    this.loadEventHistory();
    this.loadAssignments();

     this.refreshInterval = setInterval(() => {
    // ⛔️ STOP refreshing when assignment modal is open
    if (this.assignmentModalVisible) {
     //console.log('Skipping refresh - assignment modal open');
      return;
    }
    
    this.loadEventHistory();  
    this.loadAssignments();
  }, 10000);
     this.updateClock();
  this.clockInterval = setInterval(() => {
    this.updateClock();
   this.setupAssignmentProgressListener();

 
  }, 1000);
  
 //console.log('Clock initialized');
  },
  beforeDestroy() {
    if (this.socket) {
      this.socket.disconnect();
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  getDeviceRowClass({ row }) {
  return row.is_assigned ? 'assigned-device-row' : '';
},
};
</script>

<style scoped>
.border {
  border: 1px solid #dee2e6;
}
.status-indicator {
  position: relative;
  width: 40px;
  height: 40px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  position: relative;
}

.status-dot::before {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse-ring 2s ease-out infinite;
}

.status-dot-success {
  background: #2dce89;
}

.status-dot-success::before {
  border: 2px solid #2dce89;
}

.status-dot-danger {
  background: #f5365c;
}

.status-dot-danger::before {
  border: 2px solid #f5365c;
}

@keyframes pulse-ring {
  0% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(0.8);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.5);
  }
}

.btn-group {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
.provisioning-container {
  max-height: 70vh;
  overflow-y: auto;
}

.device-provision-card {
  transition: all 0.3s ease;
  border-width: 2px;
}

.device-provision-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.device-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 10px;
}

.status-message {
  padding: 8px;
  background: #f8f9fa;
  border-radius: 5px;
  font-style: italic;
}

.files-status {
  max-height: 150px;
  overflow-y: auto;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 5px;
}

.file-item {
  padding: 3px 0;
  display: flex;
  align-items: center;
}

.file-item i {
  min-width: 20px;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.device-provision-card {
  animation: slideIn 0.3s ease;
}
/* Highlight assigned devices in the table */
::v-deep .assigned-device-row {
  background-color: #e3f2fd !important; /* Light blue background */
  border-left: 4px solid #2196F3; /* Blue left border */
}

::v-deep .assigned-device-row:hover {
  background-color: #bbdefb !important;
}

/* Make assigned badge more prominent with pulse animation */
.assigned-device-row .badge-info {
  animation: pulse-badge 2s infinite;
  font-weight: bold;
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}
</style>